import { Box, TextField, Typography } from '@mui/material';
import React from 'react';
import ImageDropZone from './ImageDropZone';

/**
 * Drag-drop (or click) image upload → BunnyCDN. Writes the returned ABSOLUTE CDN URL back
 * through onChange, so images are portable across origins. The URL field stays as a manual
 * fallback (e.g. an SVG logo, which the raster-only upload deliberately doesn't accept).
 *
 * Generalized from scenes/broker-content-hub (2026-08-11): the upload endpoint is a prop, so
 * broker-content (`/api/broker-content/upload-image?kind=…`) and webinar-LP
 * (`/api/webinar-lp/upload-image?kind=…`) share one component.
 *
 * Seit 2026-09-03 ist das ein Komposit über `ImageDropZone` — die Ablegefläche wurde
 * herausgelöst, damit das Eigenschaften-Panel des Baustein-Editors sie ohne dieses URL-Feld
 * verwenden kann (dort committet jeder Tastendruck einen Undo-Schritt). Der Vertrag dieser
 * Komponente ist dabei UNVERÄNDERT geblieben; `ImageUploadField.test.tsx` hält ihn fest.
 */
interface ImageUploadFieldProps {
  label: string;
  /** Full upload endpoint incl. kind param, e.g. `/api/webinar-lp/upload-image?kind=header`. */
  uploadUrl: string;
  value: string | null;
  onChange: (url: string) => void;
  /** Sperrt Upload UND URL-Feld. Default false — bestehende Aufrufer bleiben unveraendert. */
  disabled?: boolean;
  /** Kurze Begruendung unter dem Label, wenn das Feld gesperrt ist. */
  disabledHint?: string;
}

const ImageUploadField: React.FC<ImageUploadFieldProps> = ({
  label,
  uploadUrl,
  value,
  onChange,
  disabled = false,
  disabledHint,
}) => (
  <Box>
    <Typography variant="body2" sx={{ mb: 0.5 }} color={disabled ? 'text.disabled' : undefined}>
      {label}
    </Typography>
    {disabled && disabledHint && (
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
        {disabledHint}
      </Typography>
    )}
    <ImageDropZone uploadUrl={uploadUrl} value={value} onUploaded={onChange} disabled={disabled} />
    <TextField
      disabled={disabled}
      label="Bild-URL"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      size="small"
      fullWidth
      sx={{ mt: 1 }}
      helperText="Nach Upload automatisch gesetzt — oder manuell eintragen (z. B. SVG-Logo)."
    />
  </Box>
);

export default ImageUploadField;
